import useSWR, { KeyedMutator } from 'swr';
import { useMemo } from 'react';
import { SecuritiesQuotes } from '../type';
import { Securities } from '../../enum';
import { useSecurityQuotesStringifiedParams } from './defaultParams';
import { getServiceUrl } from '../../../../shared';
import { ACTOR_DEDUPING_INTERVAL } from '../../../shared';
import { useFetchWithAuthWithSend } from '../../../shared/utils/fetchWithAuth';
import { makeApiUrl } from '../../../../../../aws';

const useMakeSecurityQuotesUrl = () => {
  const stringifiedParams = useSecurityQuotesStringifiedParams();
  const url = stringifiedParams !== null ? `${makeApiUrl('actor')}/${getServiceUrl(Securities.Quotes)}${stringifiedParams}` : null;
  return url;
};

type BaseUseSecuritiesQuoteApiResponse = {
  isLoading: boolean,
  error?: any,
  mutate: KeyedMutator<SecuritiesQuotes[]>,
  isValidating: boolean,
}

type UseSecuritiesQuotesApiResponse = {
  data: undefined | SecuritiesQuotes[],
} & BaseUseSecuritiesQuoteApiResponse;

type FilterPredicate = (securitiesQuote: SecuritiesQuotes) => boolean;

type SortPredicate = (prevSecuritiesQuote: SecuritiesQuotes, nextSecuritiesQuote: SecuritiesQuotes) => number;

type UseSecuritiesQuotesApiProps = {
  filterPredicate?: FilterPredicate,
  sortPredicate?: SortPredicate,
  withSend?: boolean,
};

export const useSecuritiesQuotesApi = (props: UseSecuritiesQuotesApiProps): UseSecuritiesQuotesApiResponse => {
  const {
    filterPredicate,
    sortPredicate,
    withSend,
  } = props;
  const url = useMakeSecurityQuotesUrl();
  const fetcher = useFetchWithAuthWithSend(withSend);
  const result = useSWR<SecuritiesQuotes[], any>(
    url,
    fetcher,
    { dedupingInterval: ACTOR_DEDUPING_INTERVAL },
  );
  const resultWithLoading = useMemo(() => {
    const isLoading = !result.data && !result.error;
    let { data } = result;
    if (data) {
      if (sortPredicate) {
        data = data.sort(sortPredicate);
      }
      if (filterPredicate) {
        data = data.filter((securityQuote) => filterPredicate(securityQuote));
      }
    }
    return ({
      ...result,
      data,
      isLoading,
    });
  }, [result, filterPredicate, sortPredicate]);
  return resultWithLoading;
};

type TransformArrayFunction<T> = (SecuritiesQuotes: SecuritiesQuotes[] | undefined) => T

type UseSecuritiesQuotesApiTransformProps<T> = {
  transformFunction: TransformArrayFunction<T>,
} & UseSecuritiesQuotesApiProps

type UseSecuritiesQuoteApiTransformResponse<T> = {
  data: T | undefined,
} & BaseUseSecuritiesQuoteApiResponse;

export function useSecuritiesQuotesTransformApi<T>(props: UseSecuritiesQuotesApiTransformProps<T>): UseSecuritiesQuoteApiTransformResponse<T> {
  const {
    filterPredicate,
    sortPredicate,
    transformFunction,
    withSend,
  } = props;
  const response = useSecuritiesQuotesApi({
    filterPredicate,
    sortPredicate,
    withSend,
  });
  const result = useMemo(() => {
    const {
      data,
      isLoading,
      error,
    } = response;
    return {
      ...response,
      data: (!isLoading && !error) ? transformFunction(data) : undefined,
    };
  }, [response, transformFunction]);
  return result;
}

type UseSecuritiesQuoteApiResponse = {
  data: SecuritiesQuotes | undefined,
} & BaseUseSecuritiesQuoteApiResponse;

export const useSecuritiesQuoteApi = (props: UseSecuritiesQuotesApiProps): UseSecuritiesQuoteApiResponse => {
  const {
    filterPredicate,
    sortPredicate,
    withSend,
  } = props;
  const url = useMakeSecurityQuotesUrl();
  const fetcher = useFetchWithAuthWithSend(withSend);
  const result = useSWR<SecuritiesQuotes[], any>(
    url,
    fetcher,
    { dedupingInterval: ACTOR_DEDUPING_INTERVAL },
  );
  const resultWithLoading = useMemo(() => {
    const isLoading = !result.data && !result.error;
    // eslint-disable-next-line prefer-destructuring
    let data: SecuritiesQuotes[] | SecuritiesQuotes | undefined = result.data;
    if (data) {
      if (sortPredicate) {
        data = data.sort(sortPredicate);
      }
      if (filterPredicate) {
        data = data.find((securityQuote) => filterPredicate(securityQuote));
      }
      if (Array.isArray(data)) {
        // eslint-disable-next-line prefer-destructuring
        data = data[0];
      }
    }
    return ({
      ...result,
      data,
      isLoading,
    });
  }, [result, filterPredicate, sortPredicate]);
  return resultWithLoading;
};

type TransformFunction<T> = (SecuritiesQuote: SecuritiesQuotes | undefined) => T

type UseSecuritiesQuoteApiTransformProps<T> = {
  transformFunction: TransformFunction<T>,
} & UseSecuritiesQuotesApiProps;

export function useSecuritiesQuoteTransformApi<T>(props: UseSecuritiesQuoteApiTransformProps<T>): UseSecuritiesQuoteApiTransformResponse<T> {
  const {
    filterPredicate,
    sortPredicate,
    transformFunction,
    withSend,
  } = props;
  const response = useSecuritiesQuoteApi({
    filterPredicate,
    sortPredicate,
    withSend,
  });
  const result = useMemo(() => {
    const {
      data,
      isLoading,
      error,
    } = response;
    return {
      ...response,
      data: !isLoading && !error ? transformFunction(data) : undefined,
    };
  }, [response, transformFunction]);
  return result;
}

type UseSecuritiesQuoteFieldApiResponse<Field extends keyof SecuritiesQuotes> = {
  data: SecuritiesQuotes[Field] | undefined,
} & BaseUseSecuritiesQuoteApiResponse;

type UseSecuritiesQuoteFieldApiProps<Field extends keyof SecuritiesQuotes> = {
  field: Field,
} & UseSecuritiesQuotesApiProps

export function useSecuritiesQuoteFieldApi<Field extends keyof SecuritiesQuotes>(props: UseSecuritiesQuoteFieldApiProps<Field>): UseSecuritiesQuoteFieldApiResponse<Field> {
  const {
    filterPredicate,
    sortPredicate,
    field,
    withSend,
  } = props;
  const response = useSecuritiesQuoteApi({
    filterPredicate,
    sortPredicate,
    withSend,
  });
  const result = useMemo(() => {
    const {
      data,
    } = response;
    return {
      ...response,
      data: data ? data[field] : undefined,
    };
  }, [response, field]);
  return result;
}
